import { FieldError, FieldErrors } from 'react-hook-form';
import { ApiError, ApiErrorMeta } from '../ApiClient';
import { parseWarningMessage } from './warningMessage';
import { parseFieldPath } from './fieldPath';

export type GetApiErrorMessage = (
  code: string,
  meta?: ApiErrorMeta,
  path?: string,
) => string;

export interface ValidationMessage {
  message: string;
  type: 'warning' | 'error';
  details?: string;
}

const getApiErrorDetails = (meta?: ApiErrorMeta) => {
  const { args } = meta ?? {};
  if (!args || Array.isArray(args)) {
    return undefined;
  }

  const { message } = args;
  return typeof message === 'string' && message.trim() ? message : undefined;
};

export class ValidationMessagesMap extends Map<string, ValidationMessage[]> {
  totalErrors = 0;
  totalWarnings = 0;

  constructor(entries: [string, ValidationMessage[]][] = []) {
    super(entries);

    entries.forEach(([_, messages]) => {
      messages.forEach(({ type }) => {
        if (type === 'error') {
          this.totalErrors++;
        } else {
          this.totalWarnings++;
        }
      });
    });
  }

  append(
    name: string,
    message: string,
    type: 'warning' | 'error',
    details?: string,
  ) {
    const messages = this.get(name) || [];
    messages.push({ message, type, details });
    this.set(name, messages);

    if (type === 'error') {
      this.totalErrors++;
    } else {
      this.totalWarnings++;
    }

    return this;
  }

  merge(map: ValidationMessagesMap) {
    map.forEach((messages, name) => {
      messages.forEach(({ message, type, details }) => {
        this.append(name, message, type, details);
      });
    });
    return this;
  }

  static fromApiErrors(
    apiErrors: ApiError[],
    getErrorMessage: GetApiErrorMessage = (code) => code,
    groupFn: (error: ApiError, index: number) => string | undefined = (error) =>
      getFieldPath(error)?.join('.'),
  ): ValidationMessagesMap {
    return apiErrors.reduce((messagesMap, apiError, i) => {
      const name = groupFn(apiError, i) || 'root';
      const { code, path, meta } = apiError;
      const message = getErrorMessage(code, meta, path);
      const type = meta?.level || 'error';
      const details = getApiErrorDetails(meta);
      return messagesMap.append(name, message, type, details);
    }, new ValidationMessagesMap());
  }

  static fromFieldErrors(
    errorsObject: FieldErrors,
    parentPath: (string | number)[] = [],
  ): ValidationMessagesMap {
    const map = new ValidationMessagesMap();

    if (isFieldError(errorsObject)) {
      map.merge(
        new ValidationMessagesMap().set(
          parentPath.join('.'),
          getMessages(errorsObject),
        ),
      );
    } else {
      for (const [fieldName, value] of Object.entries(errorsObject)) {
        if (Array.isArray(value)) {
          for (const [index, val] of value.entries()) {
            if (val) {
              map.merge(
                this.fromFieldErrors(val, [...parentPath, fieldName, index]),
              );
            }
          }
        } else if (typeof value === 'object' && value !== null) {
          if (isFieldError(value)) {
            map.merge(
              new ValidationMessagesMap().set(
                [...parentPath, fieldName].join('.'),
                getMessages(value),
              ),
            );
          } else {
            map.merge(
              this.fromFieldErrors(value as FieldErrors, [
                ...parentPath,
                fieldName,
              ]),
            );
          }
        }
      }
    }

    return map;
  }
}
export const isFieldError = (value: any): value is FieldError =>
  typeof value === 'object' &&
  value !== null &&
  typeof value.types === 'object' &&
  value.types !== null;

export const getFieldPath = (
  error: ApiError,
): (string | number)[] | undefined => {
  const { path, source } = error;
  const fieldPath = source?.pointer || path;

  if (!fieldPath) {
    return undefined;
  }

  return parseFieldPath(fieldPath);
};

export const getMessages = (fieldError: FieldError): ValidationMessage[] => {
  const messages: ValidationMessage[] = [];
  const messageCache: string[] = [];

  const add = (message: string) => {
    if (!messageCache.includes(message)) {
      const warningMessage = parseWarningMessage(message);
      messages.push({
        message: warningMessage || message,
        type: warningMessage ? 'warning' : 'error',
      });
    }
    messageCache.push(message);
  };

  if (fieldError.types) {
    [Object.values(fieldError.types).flat()]
      .flat()
      .filter((val): val is string => typeof val === 'string')
      .forEach(add);
  }

  return messages;
};
